/**
 * Shared transient-retry policy for the money path.
 *
 * Replaces the H2 hotfix `190c24ca` — inline `await sleep(400)` +
 * hardcoded 2-attempts duplicated across the PayPal SDK script load and
 * the `/api/paypal/client-token` fetch in `hooks/use-paypal-sdk.ts`.
 *
 * **Policy** (defaults match `.tondocs/infra/AUTH_REFRESH_RACE.md §"When
 * you're adding a mutating action"`):
 *   - 2 attempts total (1 original + 1 retry).
 *   - 400ms backoff between attempts.
 *   - Transient = network throw (no `status` field) OR HTTP 401 / 5xx.
 *   - Permanent = HTTP 4xx other than 401 (real client bug, fail fast).
 *
 * **Why a GET surface gets the same policy as writes** — the SDK loader's
 * `/api/paypal/client-token` GET hits the SAME Supabase refresh-token race
 * the policy was originally scoped to (writes only). The race fires
 * regardless of method; the SDK loader was the visible surface on the
 * 2026-06-26 drop (76× `auth.session_cleared` over 36h, almost all on the
 * hot listing). `.tondocs/infra/AUTH_REFRESH_RACE.md §3` was updated in
 * the same handoff to call out the SDK loader as a sanctioned exception.
 *
 * **Observability** — `onRetry` is invoked once per retry with
 * `{ stage, attempt, error }`. Hooks `console.warn` by default; callers
 * pass a custom callback to fire a telemetry event. This makes "retry
 * fired" measurable post-drop without scraping logs.
 *
 * **Not a single-flight** — the helper retries from the CALLER'S point of
 * view only. Multiple parallel mounts each run their own retry. Server-
 * side single-flight (Upstash KV lock keyed on `user_id`) is the systemic
 * fix `AUTH_REFRESH_RACE.md` documents as out-of-scope; T5 in the
 * drop-resilience handoff graduates to it if retry-fire counts justify.
 *
 * @see .tondocs/infra/AUTH_REFRESH_RACE.md (policy this helper formalises)
 * @see .tondocs/paypal/HANDOFF_DROP_RESILIENCE_2026_06_26.md T3
 * @see hooks/use-paypal-sdk.ts (consumer)
 */

/**
 * Classification verb — what the retry loop does next when `fn` throws.
 */
export type TransientClassification = "transient" | "permanent";

/**
 * Thrown by callers (typically a `fetch` wrapper) to mark a non-2xx
 * response as a retryable HTTP failure. Carries `status` so the default
 * classifier can branch on the canonical 401 / 5xx policy without
 * re-parsing.
 *
 * Network failures (`fetch` rejects on no connection / DNS / TLS) are
 * classified as transient via their bare-Error shape (no `status`),
 * matching how the H2 hotfix handled them inline.
 */
export class TransientHttpError extends Error {
  readonly status: number;
  readonly bodySnippet?: string;
  constructor(status: number, bodySnippet?: string) {
    super(`HTTP ${status}${bodySnippet ? `: ${bodySnippet.slice(0, 200)}` : ""}`);
    this.name = "TransientHttpError";
    this.status = status;
    this.bodySnippet = bodySnippet;
  }
}

/**
 * Default classifier. Used when the caller does not pass a custom
 * `classify`.
 *
 * Rules — locked to the AUTH_REFRESH_RACE policy:
 *   - `TransientHttpError` with `status === 401` → transient (auth race).
 *   - `TransientHttpError` with `status >= 500` → transient (server flake).
 *   - `TransientHttpError` with any other status → permanent (real 4xx).
 *   - Anything else (network throw, plain `Error`, undefined) → transient.
 *
 * The "anything else → transient" branch is deliberate: at the SDK init
 * path, a bare throw is almost certainly a network blip / in-app browser
 * flake / mid-flight cancellation, all of which are retryable. The cost
 * of a wrong-transient call here is a single 400ms wait + a second try;
 * the cost of a wrong-permanent call is a buyer who can't reach the Pay
 * button on a hot drop.
 */
export function classifyHttpRetry(error: unknown): TransientClassification {
  if (error instanceof TransientHttpError) {
    if (error.status === 401) return "transient";
    if (error.status >= 500) return "transient";
    return "permanent";
  }
  return "transient";
}

/**
 * Per-retry observability hook. Invoked AFTER the failure but BEFORE the
 * sleep; receives the upcoming retry number (1-indexed).
 *
 * `stage` is the caller-supplied label so post-mortem queries can split
 * "script_load" vs "client_token" without grepping the error type.
 */
export interface RetryInfo {
  stage: string;
  attempt: number;
  error: unknown;
}

export interface RetryTransientOptions {
  /**
   * Total attempts INCLUDING the original. Default 2 (= 1 retry).
   * Caller bumps this only with a written reason — bumping to 3 is a
   * change to the AUTH_REFRESH_RACE policy and must be cross-linked.
   */
  attempts?: number;
  /**
   * Sleep between attempts (ms). Default 400 — chosen so parallel-mount
   * cookies have time to stabilise (see AUTH_REFRESH_RACE.md §"When
   * you're adding a mutating action": "200ms delay — by then the parallel-
   * mount cookies have stabilised"; the SDK loader uses 400ms because
   * the script load adds its own latency).
   */
  backoffMs?: number;
  /**
   * Label for the call site. Surfaced via `onRetry` so post-mortem queries
   * can split SDK-script load from client-token fetch (etc.) without
   * grepping error types. REQUIRED — the helper is observable by design.
   */
  stage: string;
  /**
   * Telemetry hook. Default fires a single `console.warn` per retry.
   * Callers wire a custom callback to ship to Axiom / monitoring.
   */
  onRetry?: (info: RetryInfo) => void;
  /**
   * Custom classifier. Default = {@link classifyHttpRetry}.
   * Override when the caller wraps a non-HTTP API (e.g. WebSocket reconnect)
   * with different transient semantics.
   */
  classify?: (error: unknown) => TransientClassification;
}

const defaultOnRetry = (info: RetryInfo): void => {
  // eslint-disable-next-line no-console
  console.warn(
    `[retryTransient] ${info.stage} attempt ${info.attempt} failed (transient)`,
    info.error,
  );
};

/**
 * Run `fn`; on a transient failure, sleep `backoffMs` and retry until
 * `attempts` are exhausted. On a permanent failure, rethrow immediately.
 *
 * Returns whatever `fn` returns on success. Rethrows the LAST error if
 * all attempts fail (the caller's catch block sees a real error, not a
 * fabricated "out of retries").
 *
 * Cancellation: the caller is responsible for short-circuiting on a
 * cancellation signal (e.g. a React effect-cleanup flag); the helper
 * does not own that. Wrap your `fn` to no-op on a stale cleanup flag if
 * needed.
 */
export async function retryTransient<T>(
  fn: () => Promise<T>,
  options: RetryTransientOptions,
): Promise<T> {
  const attempts = options.attempts ?? 2;
  const backoffMs = options.backoffMs ?? 400;
  const classify = options.classify ?? classifyHttpRetry;
  const onRetry = options.onRetry ?? defaultOnRetry;

  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (error) {
      lastErr = error;
      const isLast = i === attempts - 1;
      if (isLast) throw error;
      if (classify(error) === "permanent") throw error;
      onRetry({ stage: options.stage, attempt: i + 1, error });
      await sleep(backoffMs);
    }
  }
  // Unreachable: the loop always returns or throws. Belt-and-braces so
  // a future refactor of the loop bounds can't silently hand back
  // `undefined` typed as T.
  throw lastErr instanceof Error
    ? lastErr
    : new Error("retryTransient: exhausted attempts without throw");
}

function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}
